<template>
  <div class="orangehrm-background-container">
    <div
      v-if="ownBalance"
      class="orangehrm-card-container handbook-own-balance"
    >
      <handbook-balance :summary="ownBalance" />
    </div>
    <div class="orangehrm-paper-container">
      <div class="orangehrm-card-container">
        <div class="handbook-approvals-header">
          <oxd-text tag="h6" class="orangehrm-main-title"
            >Leave Approvals</oxd-text
          >
          <oxd-button
            label="Refresh"
            display-type="ghost"
            :disabled="isLoading"
            @click="loadRequests"
          />
        </div>
        <oxd-text class="handbook-approvals-help">
          Leave dated January 1, 2027 onward requires Executive Director final
          approval.
        </oxd-text>
        <oxd-text
          v-if="!isLoading && !canApprove"
          class="handbook-approvals-help"
        >
          Your requests awaiting final approval appear below. Their status will
          update after Executive Director review.
        </oxd-text>
        <oxd-text v-if="error" class="handbook-approvals-error" role="alert">
          {{ error }}
        </oxd-text>
      </div>
      <table-header :selected="0" :total="total" :loading="isLoading" />
      <div class="orangehrm-container">
        <oxd-card-table
          :headers="headers"
          :items="rows"
          :selectable="false"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-text v-if="!isLoading && !error && total === 0">
          No leave requests are awaiting final approval.
        </oxd-text>
        <oxd-pagination
          v-if="pages > 1"
          v-model:current="page"
          :length="pages"
        />
      </div>
    </div>
    <teleport to="#app">
      <oxd-dialog
        v-if="selectedRequest"
        :style="{width: '600px', maxWidth: '95vw'}"
        @update:show="closeAction"
      >
        <oxd-text tag="h6">
          {{
            action === 'view'
              ? 'Leave Request Details'
              : action === 'approve'
              ? 'Approve Leave'
              : 'Reject Leave'
          }}
        </oxd-text>
        <oxd-divider />
        <div
          v-if="selectedRequest.comments && selectedRequest.comments.length"
          class="handbook-approvals-comments"
        >
          <oxd-text type="card-title">Comments and notice</oxd-text>
          <oxd-text
            v-for="(entry, index) in selectedRequest.comments"
            :key="index"
          >
            {{ entry }}
          </oxd-text>
        </div>
        <oxd-text class="handbook-approvals-help">
          {{ selectedRequest.employee }} · {{ selectedRequest.leaveType }}
        </oxd-text>
        <oxd-text class="handbook-approvals-help">
          {{ selectedRequest.dates }} · {{ selectedRequest.days }} day(s)
        </oxd-text>
        <oxd-text v-if="action !== 'view'" class="handbook-approvals-help">
          {{
            action === 'approve'
              ? 'Give final Executive Director approval for this leave request.'
              : 'Reject this leave request and provide a reason for the employee.'
          }}
        </oxd-text>
        <oxd-form
          v-if="action !== 'view'"
          :loading="isSaving"
          @submit-valid="submitAction"
        >
          <oxd-form-row>
            <oxd-input-field
              v-model="comment"
              type="textarea"
              :label="action === 'reject' ? 'Reason' : 'Comment (optional)'"
              :rules="commentRules"
              :required="action === 'reject'"
            />
          </oxd-form-row>
          <oxd-text
            v-if="actionError"
            class="handbook-approvals-error"
            role="alert"
          >
            {{ actionError }}
          </oxd-text>
          <oxd-divider />
          <oxd-form-actions>
            <oxd-button
              type="button"
              label="Cancel"
              display-type="ghost"
              :disabled="isSaving"
              @click="closeAction"
            />
            <submit-button
              :label="action === 'approve' ? 'Approve' : 'Reject'"
              :disabled="isSaving"
            />
          </oxd-form-actions>
        </oxd-form>
        <oxd-button
          v-if="action === 'view'"
          label="Close"
          display-type="ghost"
          @click="closeAction"
        />
      </oxd-dialog>
    </teleport>
  </div>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {
  required,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';
import {remoteWorkError} from '@/orangehrmLeavePlugin/util/remote-work';
import HandbookBalance from '@/orangehrmLeavePlugin/components/HandbookBalance.vue';

const approvalPath = '/api/v2/leave/handbook-approvals';

export default {
  name: 'HandbookLeaveApprovals',
  components: {'oxd-dialog': OxdDialog, HandbookBalance},
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, approvalPath);
    http.setIgnorePath(approvalPath);
    const {jsDateFormat} = useDateFormat();
    return {http, jsDateFormat};
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      error: '',
      actionError: '',
      canApprove: false,
      ownBalance: null,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 25,
      selectedRequest: null,
      action: null,
      comment: '',
    };
  },
  computed: {
    pages() {
      return Math.ceil(this.total / this.pageSize);
    },
    commentRules() {
      return this.action === 'reject'
        ? [required, shouldNotExceedCharLength(200)]
        : [shouldNotExceedCharLength(200)];
    },
    headers() {
      const headers = [
        {name: 'employee', title: 'Employee', style: {flex: 1.5}},
        {name: 'leaveType', title: 'Leave type', style: {flex: 1.5}},
        {name: 'dates', title: 'Dates', style: {flex: 2}},
        {name: 'days', title: 'Days', style: {flex: 1}},
      ];
      const cellConfig = {
        view: {
          onClick: (row) => this.openAction(row, 'view'),
          component: 'oxd-button',
          props: {label: 'View details', displayType: 'text', size: 'medium'},
        },
      };
      if (this.canApprove) {
        Object.assign(cellConfig, {
          approve: {
            onClick: (row) => this.openAction(row, 'approve'),
            component: 'oxd-button',
            props: {label: 'Approve', displayType: 'text', size: 'medium'},
          },
          reject: {
            onClick: (row) => this.openAction(row, 'reject'),
            component: 'oxd-button',
            props: {label: 'Reject', displayType: 'text', size: 'medium'},
          },
        });
      }
      headers.push({
        name: 'actions',
        title: 'Actions',
        slot: 'footer',
        cellType: 'oxd-table-cell-actions',
        style: {flex: 2},
        cellConfig,
      });
      return headers;
    },
  },
  watch: {
    page() {
      this.loadRequests();
    },
  },
  beforeMount() {
    this.loadRequests();
  },
  methods: {
    async loadRequests() {
      this.isLoading = true;
      this.error = '';
      try {
        const {data: response} = await this.http.getAll({
          limit: this.pageSize,
          offset: (this.page - 1) * this.pageSize,
        });
        const {data} = response;
        this.canApprove = data.canApprove === true;
        this.ownBalance = data.ownBalance || null;
        this.total = data.total;
        this.rows = data.requests.map((request) => ({
          ...request,
          dates: `${this.displayDate(request.fromDate)} – ${this.displayDate(
            request.toDate,
          )}`,
          days: Number(request.days).toFixed(2),
        }));
        if (this.page > Math.max(1, this.pages)) {
          this.page = Math.max(1, this.pages);
        }
      } catch (error) {
        this.rows = [];
        this.total = 0;
        this.canApprove = false;
        this.ownBalance = null;
        this.error = remoteWorkError(
          error,
          'Pending leave requests could not be loaded. Please try refreshing.',
        );
      } finally {
        this.isLoading = false;
      }
    },
    displayDate(date) {
      return formatDate(parseDate(date), this.jsDateFormat) || date;
    },
    openAction(request, action) {
      if (!['view', 'approve', 'reject'].includes(action)) return;
      if (action !== 'view' && !this.canApprove) return;
      this.selectedRequest = request;
      this.action = action;
      this.comment = '';
      this.actionError = '';
    },
    closeAction() {
      if (this.isSaving) return;
      this.selectedRequest = null;
      this.action = null;
      this.comment = '';
      this.actionError = '';
    },
    async submitAction() {
      if (
        this.isSaving ||
        !this.canApprove ||
        !this.selectedRequest ||
        !['approve', 'reject'].includes(this.action)
      )
        return;
      this.isSaving = true;
      this.actionError = '';
      try {
        await this.http.update(this.selectedRequest.id, {
          action: this.action,
          comment: this.comment.trim(),
        });
        this.$toast.updateSuccess();
        this.selectedRequest = null;
        this.action = null;
        this.comment = '';
        await this.loadRequests();
      } catch (error) {
        this.actionError = remoteWorkError(
          error,
          'The decision could not be saved. This request may have changed. Close this dialog and refresh the queue before trying again.',
        );
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.handbook-own-balance {
  margin-bottom: 1rem;
}
.handbook-approvals-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.handbook-approvals-help {
  margin: 0.75rem 0;
  line-height: 1.6;
}
.handbook-approvals-error {
  padding: 1rem;
  margin: 1rem 0;
  border-left: 3px solid $oxd-feedback-danger-color;
}
.handbook-approvals-comments {
  margin: 1rem 0;
  .oxd-text {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    margin-top: 0.5rem;
  }
}
</style>
